import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, UploadCloud, ScrollText, Users, Database } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { AppShell } from "@/components/shell/AppShell";
import { useModules, iconOf } from "@/hooks/useModules";
import { Badge } from "@/components/kit";
import api from "@/lib/api";

const monthLabel = (m) => (m ? new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleString("en", { month: "short", year: "numeric" }) : null);

export default function HubPage() {
  const { user } = useAuth();
  const { tenantId, tenant } = useTenant() || {};
  const { modules } = useModules();
  const [latest, setLatest] = useState(null);
  const firstName = user?.name?.split(" ")[0] || "there";
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const load = useCallback(async () => {
    if (!tenantId || !modules) return;
    const pairs = await Promise.all(modules.filter((m) => m.connected && m.allowed).map((m) =>
      api.get(`/uploads/${m.slug}/ledger?tenant_id=${tenantId}`).then((l) => [m.slug, l.data]).catch(() => [m.slug, []])));
    setLatest(Object.fromEntries(pairs));
  }, [tenantId, modules]);
  useEffect(() => { load(); }, [load]);
  const newest = Object.values(latest || {}).flat().map((l) => l.month).sort().slice(-1)[0];

  return (
    <AppShell back={false}>
      <div className="mk-fade mb-8">
        <p className="mk-label tracking-[0.3em]">おかえりなさい · Welcome back</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <h1 data-testid="hub-greeting" className="font-heading text-4xl font-extrabold tracking-tight text-thstrong sm:text-5xl">
            Hello, <span className="text-thcyan">{firstName}</span>
          </h1>
          <p data-testid="hub-context" className="mk-sub pb-2">
            {tenant ? `${tenant.name} · ` : ""}{newest ? `Latest upload ${monthLabel(newest)}` : "No months uploaded yet"} · {(modules || []).filter((m) => m.allowed).length} modules
          </p>
        </div>
      </div>

      <p className="mk-label mb-4">Modules</p>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {(modules || []).map((tool, i) => {
          const { slug, name } = tool;
          const ok = tool.allowed;
          const Icon = iconOf(tool.icon);
          const months = latest?.[slug] || [];
          const last = months.slice(-1)[0];
          const Wrapper = ok ? Link : "div";
          return (
            <Wrapper key={slug} to={ok ? `/module/${slug}` : undefined} data-testid={`tool-card-${slug}`}
              className={`mk-card mk-fade group flex flex-col p-6 ${ok ? "mk-card-hover" : "opacity-55"}`} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="mb-5 flex items-start justify-between">
                <span className="mk-icon-tile h-11 w-11 rounded-xl" style={{ color: tool?.color || "#7a8fb5", background: `${tool?.color || "#7a8fb5"}1f` }}><Icon className="h-5 w-5" /></span>
                {ok ? <ArrowUpRight className="h-4 w-4 text-thdim transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-thcyan" /> : <Badge testid={`locked-${slug}`}><Lock className="h-2.5 w-2.5" /> Locked</Badge>}
              </div>
              <h3 className="font-heading text-lg font-bold text-thstrong">{name}</h3>
              <p className="mt-1 flex-1 text-sm leading-relaxed text-thmute">{tool?.desc || "Not available for your role on this client."}</p>
              <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-thborder/8 pt-4">
                {!ok ? <span className="mk-sub">Ask your administrator for access</span> : tool?.connected && latest === null ? (
                  <span className="mk-skel h-5 w-24" />
                ) : tool?.connected ? (
                  <>
                    <Badge tone="good"><span className="h-1.5 w-1.5 rounded-full bg-thmint" /> {months.length} month{months.length === 1 ? "" : "s"}</Badge>
                    {last && <span className="mk-sub">Latest {monthLabel(last.month)} · {Object.keys(last.rows_per_tab || {}).length} feeds{tool?.agents ? ` · ${tool.agents} agent${tool.agents === 1 ? "" : "s"}` : ""}</span>}
                  </>
                ) : (
                  <Badge tone="neutral">No data yet</Badge>
                )}
              </div>
            </Wrapper>
          );
        })}
      </div>

      {isAdmin && (
        <div className="mt-10">
          <p className="mk-label mb-4">Quick actions</p>
          <div className="flex flex-wrap gap-3" data-testid="hub-quick-actions">
            {[["uploads", "Upload data", UploadCloud], ["activity", "View activity log", ScrollText], ["users", "Manage team", Users]].map(([s, l, I]) => (
              <Link key={s} to={`/settings?section=${s}`} data-testid={`quick-${s}`} className="mk-pill"><I className="h-3.5 w-3.5 text-thcyan" /> {l}</Link>
            ))}
          </div>
        </div>
      )}
      <p className="mk-sub mt-12 flex items-center gap-2"><Database className="h-3 w-3" /> Full data ownership stays with the client · dashboards build from the months you upload</p>
    </AppShell>
  );
}
